import { ReactElement, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Typography, useMediaQuery } from '@mui/material';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { featureConfig } from 'modules/common/featureConfig';
import { DashboardRoutesConfig } from 'modules/dashboard/Routes';
import { DeFiRoutesConfig } from 'modules/defi/Routes';
import { DelegateStakingRoutesConfig } from 'modules/delegateStaking/Routes';
import { useTranslation } from 'modules/i18n';
import {
  LiquidStakingRoutesConfig,
  STAKING_PATH,
} from 'modules/liquidStaking/Routes';
import { useGetOwnerPoolAddressesQuery } from 'modules/ownerPanel/actions/getOwnerPoolAddresses';
import { OwnerPanelRoutesConfig } from 'modules/ownerPanel/Routes';
import {
  EMPTY_POOL_ADDRESSES,
  useGetPoolAddressesQuery,
} from 'modules/pool/actions/getPoolAddresses';
import { PoolRoutesConfig } from 'modules/pool/Routes';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IMenuProps {
  className?: string;
  onClose?: () => void;
}

export function Menu({ className, onClose }: IMenuProps): ReactElement | null {
  const { t, keys } = useTranslation(translation);

  const { classes, cx, theme } = useStyles();
  const isMd = useMediaQuery(theme.breakpoints.down('md'));

  const location = useLocation();

  const isDelegateStaking = useMemo(() => {
    return location.pathname.startsWith(`${PoolRoutesConfig.pool.path}`);
  }, [location.pathname]);

  const isStaking = useMemo(() => {
    return location.pathname.startsWith(`${STAKING_PATH}/`);
  }, [location.pathname]);

  const { isConnected, connectionKey } = useConnection();

  const { data: pools = EMPTY_POOL_ADDRESSES } = useGetPoolAddressesQuery();
  const { data: ownerPools } = useGetOwnerPoolAddressesQuery(
    { connectionKey, poolAddresses: pools },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
    },
  );

  return (
    <div className={cx(classes.root, className)}>
      {featureConfig.isDashboardEnabled && (
        <Typography
          className={classes.link}
          component={NavLink}
          to={DashboardRoutesConfig.dashboard.generatePath()}
          variant={isMd ? 'h1' : 'body1'}
          onClick={isMd ? onClose : undefined}
        >
          {t(keys.dashboard)}
        </Typography>
      )}

      <Typography
        className={cx(classes.link, isDelegateStaking && classes.active)}
        component={NavLink}
        to={DelegateStakingRoutesConfig.staking.generatePath()}
        variant={isMd ? 'h1' : 'body1'}
        onClick={isMd ? onClose : undefined}
      >
        {t(keys.delegateStaking)}
      </Typography>

      {featureConfig.isLiquidStakingEnabled && (
        <Typography
          className={cx(classes.link, isStaking && classes.active)}
          component={NavLink}
          to={LiquidStakingRoutesConfig.dashboard.generatePath()}
          variant={isMd ? 'h1' : 'body1'}
          onClick={isMd ? onClose : undefined}
        >
          {t(keys.staking)}
        </Typography>
      )}

      {featureConfig.isDeFiEnabled && (
        <Typography
          className={classes.link}
          component={NavLink}
          to={DeFiRoutesConfig.defi.generatePath()}
          variant={isMd ? 'h1' : 'body1'}
          onClick={isMd ? onClose : undefined}
        >
          {t(keys.defi)}
        </Typography>
      )}

      {!!ownerPools?.length && (
        <Typography
          className={classes.link}
          component={NavLink}
          to={OwnerPanelRoutesConfig.panel.generatePath()}
          variant={isMd ? 'h1' : 'body1'}
          onClick={isMd ? onClose : undefined}
        >
          {t(keys.poolOwnerPanel)}
        </Typography>
      )}
    </div>
  );
}
